import { useAtom, useAtomValue } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
    AddCircledIcon,
    BasicDropdownMenu,
    type BasicMenuItemProps,
    Button,
    Dropdown,
    NotificationManager,
    TagIcon,
    Toast,
    UnifiedInputField,
} from '@placer-ui/components';
import { useGetTagsQuery } from 'hooks/queries/tags/use-get-tags-query';
import {
    isUpdateTagEnabledStore,
    supportCreateTagOpenStore,
    supportTableTagIdStore,
    supportTagToUpdateStore,
    tempDataTableStore,
} from 'features/my-zone/support-requests/store/property-store';
import { goToInANewTab } from 'router/go-to';
import { useResetAllPropertiesValues } from 'utils/property-utils/use-get-all-property-values';
import type { PlaceType } from '@placer-ui/types';
import type { Entity } from 'types/entity';
import { getFeatureFlags } from 'core/flow-control';
import { useCreateTagMutation } from 'hooks/queries/tags/use-create-tag';
import { useDeleteTagMutation } from 'hooks/queries/tags/use-delete-tag';

import styles from './save-as-tag-button.module.scss';
import { useUpdateTagMembersMutation } from 'hooks/queries/tags/use-update-tag-members';
import type { AddTagMembersRequest, RemoveTagMembersRequest } from 'API/tags-api';

const TOAST_DURATION = 60 * 60 * 1000; // 1 hour
const POIS_LIMIT = 2000;
const LIMIT_TOOLTIP_TEXT = `This action doesn't support more than ${POIS_LIMIT} properties. Please contact support if needed.`;
let isRetried = false;

export const SaveAsTagButton = () => {
    const [search, setSearch] = useState('');
    const [isInlineModeVisible, setIsInlineModeVisible] = useState(false);
    const [newName, setNewName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [tagToUpdate, setTagToUpdate] = useAtom(supportTagToUpdateStore);
    const [createTagIsOpen, setCreateTagIsOpen] = useAtom(supportCreateTagOpenStore);
    const [supportTableTagId, setSupportTableTagId] = useAtom(supportTableTagIdStore);
    const [savedProperties, setSavedProperties] = useState<Entity[]>([]);
    const [toastsIds, setToastsIds] = useState<string[]>([]);
    const [isUpdateTagEnabled, setIsUpdateTagEnabled] = useAtom(isUpdateTagEnabledStore);
    const tempDataTable = useAtomValue(tempDataTableStore);
    const { enable_limit_add_to_tag_ff } = getFeatureFlags();

    const { data: tags, refetch: refetchGetTags } = useGetTagsQuery();
    const refetchTagProperties = useResetAllPropertiesValues();

    const { mutateAsync: createTag } = useCreateTagMutation();
    const { mutateAsync: updateTagMembersMutation } = useUpdateTagMembersMutation();
    const { mutateAsync: deleteTag } = useDeleteTagMutation();

    const notificationManager = NotificationManager.getInstance();

    const tagsItems: BasicMenuItemProps[] =
        tags?.personalTags?.map((tag) => ({
            label: tag.name,
            value: tag.id,
        })) || [];

    const availableProperties = useMemo(
        () =>
            tempDataTable
                ?.filter(
                    ({ status, entity_type, entity_id }) =>
                        status === 'Available' && entity_type && entity_id,
                )
                .map(({ entity_id, entity_type }) => ({
                    entity_id: entity_id as string,
                    entity_type: entity_type as PlaceType,
                })),
        [tempDataTable],
    );

    const hasChanges = useMemo(() => {
        if (!savedProperties?.length || !availableProperties) {
            return false;
        }

        if (savedProperties.length !== availableProperties.length) {
            return true;
        }
        const savedEntitiesSet = new Set(savedProperties.map(({ entity_id }) => entity_id));

        return availableProperties.some(({ entity_id }) => !savedEntitiesSet.has(entity_id));
    }, [availableProperties, savedProperties]);

    useEffect(() => {
        isRetried = false;
    }, [availableProperties]);

    useEffect(() => {
        setIsUpdateTagEnabled(hasChanges);
    }, [hasChanges, setIsUpdateTagEnabled]);

    const handleCloseToast = (id: string) => {
        notificationManager.removeNotification(id);
        setToastsIds(toastsIds.filter((toastId) => toastId !== id));
    };

    const successToast = (tagToDelete?: string) => {
        const id = `support-page-complete-toast-${uuidv4()}`;
        setToastsIds([...toastsIds, id]);

        return notificationManager.addNotification({
            type: 'toast',
            title: availableProperties?.length
                ? `All ${availableProperties?.length} 'Available' properties have been added to the tag.`
                : 'All newly added properties will be automatically included in your selected tag.',
            duration: TOAST_DURATION,
            id,
            testId: id,
            icon: 'approve',
            isInStack: true,
            children: (
                <>
                    {tagToDelete && (
                        <Toast.CtaButton
                            testId={`${id}-undo`}
                            onClick={() => {
                                handleDeleteTag(tagToDelete);
                                handleCloseToast(id);
                            }}
                        >
                            Undo
                        </Toast.CtaButton>
                    )}
                    <Toast.CtaButton testId={`${id}-dismiss`} onClick={() => handleCloseToast(id)}>
                        Dismiss
                    </Toast.CtaButton>
                </>
            ),
        });
    };

    const failedToast = (
        type: 'update' | 'create',
        defaultRetried: boolean,
        tagForRetry?: string,
    ) => {
        const id = `support-page-error-toast-${uuidv4()}`;
        setToastsIds([...toastsIds, id]);

        return notificationManager.addNotification({
            type: 'toast',
            title:
                type === 'create'
                    ? 'We couldn’t create the tag. Please retry.'
                    : availableProperties?.length
                    ? `We couldn't add all ${availableProperties?.length} 'Available' properties\n to the tag. Please retry.`
                    : 'We couldn’t update the tag. Please retry.',
            id,
            testId: id,
            duration: TOAST_DURATION,
            icon: 'error',
            isInStack: true,
            children: (
                <>
                    <Toast.CtaButton
                        testId={`${id}-${defaultRetried ? 'dismiss' : 'retry'}`}
                        onClick={() => {
                            if (!defaultRetried && tagForRetry) {
                                handleUpdateTag(tagForRetry);
                                isRetried = true;
                            }
                            handleCloseToast(id);
                        }}
                    >
                        {defaultRetried ? 'Dismiss' : 'Retry'}
                    </Toast.CtaButton>
                    <Toast.CtaButton
                        testId={`${id}-contact-support`}
                        onClick={() => {
                            goToInANewTab('my-zone/my-requests/support-requests');
                            handleCloseToast(id);
                        }}
                    >
                        Contact Support
                    </Toast.CtaButton>
                </>
            ),
        });
    };

    useEffect(() => {
        return () => {
            toastsIds.forEach((id) => notificationManager.removeNotification(id));
        };
    }, [notificationManager, toastsIds]);

    const handleCreateTag = async () => {
        setIsLoading(true);

        try {
            const { data } = await createTag({
                name: newName,
                entities: availableProperties || [],
            });

            setSupportTableTagId(data.id);
            availableProperties?.length
                ? setSavedProperties([...availableProperties])
                : setSavedProperties([]);
            setIsInlineModeVisible(false);
            setNewName('');
            refetchGetTags();
            isRetried = false;
            successToast(data.id);
        } catch (error) {
            console.error(error);
            failedToast('create', true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteTag = async (tagId: string) => {
        try {
            await deleteTag(tagId);
            refetchGetTags();
            setSupportTableTagId(undefined);
        } catch (error) {
            console.error(error);
        }
    };

    const handleUpdateTag = async (tagId: string) => {
        setCreateTagIsOpen(false);

        if (!tagId) return;

        setIsLoading(true);

        const availablePropertiesSet = new Set(
            availableProperties!.map(({ entity_id }) => entity_id),
        );

        // filtered entities for adding to the tag when not exists in saved properties
        const entitiesToAdd = (availableProperties ?? []).reduce<AddTagMembersRequest['members']>(
            (members, { entity_id, entity_type }) => {
                if (!savedProperties?.some((e) => e.entity_id === entity_id)) {
                    members.push({ member_id: entity_id, member_type: entity_type });
                }
                return members;
            },
            [],
        );

        // filtered entities for removing from the tag when not exists in available properties
        const entitiesToRemove = savedProperties.reduce<RemoveTagMembersRequest['members']>(
            (members, { entity_id, entity_type }) => {
                if (!availablePropertiesSet?.has(entity_id)) {
                    members.push({ member_id: entity_id, member_type: entity_type });
                }
                return members;
            },
            [],
        );

        try {
            await updateTagMembersMutation({
                tagId,
                add: entitiesToAdd,
                remove: entitiesToRemove,
            });

            setSupportTableTagId(tagId);
            availableProperties?.length
                ? setSavedProperties([...availableProperties])
                : setSavedProperties([]);
            isRetried = false;
            successToast();
            refetchTagProperties(tagId);
            refetchGetTags();
        } catch (error) {
            console.error(error);
            failedToast('update', isRetried, tagId);
        } finally {
            setIsLoading(false);
            setTagToUpdate(undefined);
        }
    };

    useEffect(() => {
        if (tagToUpdate && !isLoading) {
            handleUpdateTag(tagToUpdate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tagToUpdate]);

    const buttonText = useMemo(() => {
        if (isLoading) {
            return 'Saving Tag';
        }
        if (supportTableTagId) {
            if (isUpdateTagEnabled) {
                return 'Update Tag';
            }
            return 'Saved as Tag';
        }

        return 'Save as Tag';
    }, [isLoading, isUpdateTagEnabled, supportTableTagId]);

    const showLimitTooltip = enable_limit_add_to_tag_ff && (tempDataTable?.length || 0) > 2000;
    const isButtonDisabled =
        isLoading || (!isUpdateTagEnabled && !!supportTableTagId) || showLimitTooltip;

    return (
        <Dropdown
            trigger={
                <Button
                    tooltip={{
                        title: showLimitTooltip ? LIMIT_TOOLTIP_TEXT : '',
                        tooltipPositioning: {
                            appendToParent: true,
                        },
                    }}
                    size="small"
                    testId="support-results-update-tag"
                    variant="secondary-gray"
                    LeftIcon={<TagIcon />}
                    isLoading={isLoading}
                    disabled={isButtonDisabled}
                    onClick={(e) => {
                        if (supportTableTagId && isUpdateTagEnabled) {
                            e.stopPropagation();
                            handleUpdateTag(supportTableTagId);
                        }
                    }}
                >
                    {buttonText}
                </Button>
            }
            appendToParent
            align="end"
            menuWidth={320}
            onInlineSearch={setSearch}
            open={createTagIsOpen}
            setOpen={setCreateTagIsOpen}
            CTA={
                isInlineModeVisible
                    ? undefined
                    : {
                          children: 'Create a New Tag',
                          onClick: () => setIsInlineModeVisible(true),
                          variant: 'text',
                          LeftIcon: <AddCircledIcon />,
                      }
            }
            hasCustomItem={false}
            isInlineModeVisible={isInlineModeVisible}
            inlineMode={{
                children: (
                    <div className={styles.inputContainer}>
                        <UnifiedInputField
                            placeholder="Type a Tag Name"
                            onChange={setNewName}
                            value={newName}
                            testId="support-results-update-tag-input"
                            name="support-results-update-tag-input"
                        />
                    </div>
                ),
                footer: {
                    submit: {
                        onClick: handleCreateTag,
                        testId: 'support-results-update-tag-submit',
                        text: 'Create Tag',
                        disabled: !newName,
                    },
                    cancel: {
                        onClick: () => {
                            setTimeout(() => setIsInlineModeVisible(false), 20);
                            setTimeout(() => setCreateTagIsOpen(true), 40);
                        },
                        testId: '',
                        text: 'Cancel',
                    },
                    layout: 'space',
                },
                header: { Icon: <></>, title: 'New Tag' },
            }}
        >
            <BasicDropdownMenu
                items={tagsItems}
                searchTerm={search}
                onItemSelection={({ value }) => handleUpdateTag(value)}
            />
        </Dropdown>
    );
};
